// The API client.
//
// Two config values have already been set correctly and consumed by nothing in
// this project -- CORS_ALLOWED_ORIGINS on the API, and VITE_API_BASE_URL here.
// Both times the symptom was a browser failure with nothing wrong in any log,
// because from the server's side nothing WAS wrong. So this module fails
// loudly at import rather than letting an undefined base URL become a request
// to the wrong origin.

import {
  accessToken,
  chooseOrganization,
  organization as organizationHeader,
} from '../auth/session';

const BASE = import.meta.env.VITE_API_BASE_URL;

if (!BASE) {
  throw new Error(
    'VITE_API_BASE_URL is not set. Vite inlines env vars at BUILD time, so ' +
      'setting it after a build has no effect -- rebuild the Pages project ' +
      'after adding it.',
  );
}

export const API_BASE_URL: string = BASE;

export type Health = { status: string };
export type Me = {
  user_id: string;
  organization_id: string;
  role: 'owner' | 'admin' | 'manager' | 'inspector';
  permissions: string[];
};
export type Version = { version: string; commit: string; environment: string };

/**
 * A request the API refused because the subject belongs to more than one
 * organization and has not said which.
 *
 * Not an error to display. It is the API asking a question, and the shell
 * answers it by rendering a picker — so it is its own type rather than a
 * string a screen has to parse out of a message.
 */
/** One organization the account may act in. The id is what the server
 *  verifies; the name is a label, and may be absent. */
export type OrganizationChoice = { id: string; name: string | null };

export class ChooseOrganization extends Error {
  constructor(readonly organizations: OrganizationChoice[]) {
    super('This account belongs to more than one organization.');
  }
}

/** The session has ended, or never started. The shell redirects to sign-in. */
export class Unauthenticated extends Error {}

/**
 * The API could not be reached, or answered with a server error.
 *
 * Its own type because the right response is the opposite of the one for a
 * 401: wait and retry, and on no account destroy the session. Render cold-
 * starts take about thirty seconds, and signing somebody out over that turns a
 * wake-up into an outage — which is the Sprint 1 rule this product has held
 * since the shell existed.
 */
export class Unreachable extends Error {}

/**
 * Every request carries the bearer token and, once chosen, the organization.
 *
 * `credentials: 'include'` is gone. It was doing nothing — the API reads a
 * bearer token, not a cookie — and it made every request a credentialed
 * cross-origin request, which is the shape that forces the CORS preflight to
 * agree about `Access-Control-Allow-Credentials` for no benefit.
 */
async function headers(extra?: HeadersInit): Promise<HeadersInit> {
  const token = await accessToken();
  const organization = organizationHeader();
  return {
    Accept: 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(organization ? { 'X-Organization-Id': organization } : {}),
    ...(extra ?? {}),
  };
}

/**
 * Turn the API's refusals into types the shell can act on.
 *
 * A 401 and a 409-with-choices are not failures to show the user: one means
 * sign in again, the other means answer a question. Leaving them as
 * `Error("HTTP 401")` pushes that decision into every screen, and one screen
 * always gets it wrong.
 */
async function refuse(res: Response, path: string): Promise<never> {
  const correlationId = res.headers.get('x-correlation-id') ?? 'unknown';
  if (res.status === 401) throw new Unauthenticated('Your session has ended.');
  if (res.status >= 500) {
    // A provider failure is a 502 carrying a code — the mail service refused,
    // with its status. Kept, so the screen can say which; a bare 500 still
    // says only that the service failed.
    const failed = refusalCode(await res.json().catch(() => null));
    throw new Unreachable(
      failed
        ? `${failed} (ref ${correlationId})`
        : `The service returned ${res.status} (ref ${correlationId}).`,
    );
  }

  // READ ONCE. A response body is a stream and can be consumed exactly once,
  // so the 409 branch reading it left nothing for the refusal code below —
  // `clone()` after the fact throws "Response body is already used", and the
  // browser suite caught it as a TypeError shown to the user in place of the
  // refusal. Parsing here means every branch reads the same object.
  const body = await res.json().catch(() => null);

  if (res.status === 409) {
    const detail = body?.detail ?? body;
    if (detail?.code === 'organization_not_chosen') {
      // Older responses sent bare identifiers. Accepting both means a browser
      // holding a cached bundle does not break the moment the API is deployed.
      const choices: OrganizationChoice[] = (detail.organizations ?? []).map(
        (o: string | OrganizationChoice) => (typeof o === 'string' ? { id: o, name: null } : o),
      );
      throw new ChooseOrganization(choices);
    }
  }
  // A 403 while carrying a stored organization: forget it.
  //
  // The id is persisted so a choice survives a reload, and it was persisted
  // forever — including after the organization stopped existing. A tenant left
  // behind by a test run, later cleaned up, sat in one browser for weeks and
  // every request carried it. `resolve()` checks membership before anything
  // else, so the answer was a 403 that named nothing, on every screen, with no
  // way out but a browser console.
  //
  // The API cannot say *which* refusal it made, deliberately. But an
  // organization this session cannot use is worth nothing whatever the reason
  // — a deactivated membership, a deleted tenant, an id from another
  // environment — and dropping it turns the next request into a 409, which is
  // the picker. Wrong once, then asked; rather than wrong forever, silently.
  //
  // Only when one was actually sent. Clearing on a 403 that carried no
  // organization would be deleting something the user just chose because a
  // different check refused them.
  if (res.status === 403 && organizationHeader()) {
    chooseOrganization(null);
  }

  // The correlation id is always surfaced so a user can quote it and it can be
  // found in the API's log.
  //
  // A REFUSAL CODE IS SHOWN; PROSE IS NOT. This function used to discard every
  // detail, which is right for an internal error and wrong for a decision:
  // when the API refuses a completion because the action is High and carries
  // no evidence, it says `evidence_required`, and that is the one thing the
  // person needs to read. Telling them "HTTP 422" instead leaves them pressing
  // the same button.
  //
  // The line between the two is drawn by SHAPE, not by a list this file would
  // have to keep in step with the API's: a code is a short lower_snake token,
  // optionally qualified after a colon (`illegal_transition:open->completed`).
  // Anything else — a sentence, a stack trace, a database message, a path — is
  // dropped, so a detail nobody intended as a code cannot become one by
  // accident.
  const code = refusalCode(body);
  throw new Error(
    code
      ? `${code} (ref ${correlationId})`
      : `${path} failed: HTTP ${res.status} (ref ${correlationId})`,
  );
}

/** The `detail` of a refusal, when it is a code rather than prose. */
const CODE = /^[a-z][a-z0-9_]{2,60}(:[A-Za-z0-9_.>-]{1,80})?$/;

function refusalCode(body: { detail?: unknown } | null): string | null {
  const detail = body?.detail;
  // Two shapes are served: a bare string (`illegal_transition:open->completed`)
  // and `{ code }`, which the documents module has sent since Sprint 2. Only
  // the first was read, so every document refusal reached a person as
  // "HTTP 409" — including the one that explains why a retry was refused.
  const candidate =
    typeof detail === 'string'
      ? detail
      : detail !== null &&
          typeof detail === 'object' &&
          typeof (detail as { code?: unknown }).code === 'string'
        ? (detail as { code: string }).code
        : null;
  return candidate !== null && CODE.test(candidate) ? candidate : null;
}

async function send<T>(method: string, path: string, body: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE_URL}${path}`, {
      method,
      headers: await headers({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(body),
    });
  } catch {
    // As `get` and `upload` already did. The browser's own "Failed to fetch"
    // reached a person verbatim from every form that saves.
    throw new Unreachable('The service could not be reached.');
  }
  if (!res.ok) await refuse(res, path);
  // 204 on a delete or a state change that returns nothing.
  return res.status === 204 ? (undefined as T) : ((await res.json()) as T);
}

/**
 * A multipart POST.
 *
 * Its own function rather than a flag on `send`, because the one thing it must
 * not do is set `Content-Type`. `multipart/form-data` carries a boundary the
 * browser generates, and a hand-written header omits it — the request then
 * arrives with a content type the server believes and a body it cannot parse,
 * which surfaces as a 422 about a missing field.
 */
async function upload<T>(path: string, form: FormData): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE_URL}${path}`, {
      method: 'POST',
      headers: await headers(),
      body: form,
    });
  } catch {
    throw new Unreachable('The service could not be reached.');
  }
  if (!res.ok) await refuse(res, path);
  return res.status === 204 ? (undefined as T) : ((await res.json()) as T);
}

/**
 * Bytes, not JSON — a document download.
 *
 * Through the API rather than a signed URL, because the API re-authorizes and
 * audits every download (E03-S05) and a signed URL is the one path that can do
 * neither. The filename comes from `Content-Disposition` when the browser is
 * allowed to read it, and from the caller otherwise.
 */
async function blob(path: string): Promise<{ blob: Blob; filename: string | null }> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE_URL}${path}`, { headers: await headers() });
  } catch {
    throw new Unreachable('The service could not be reached.');
  }
  if (!res.ok) await refuse(res, path);
  const disposition = res.headers.get('Content-Disposition') ?? '';
  const match = /filename="([^"]+)"/.exec(disposition);
  // RFC 6266: the real name, percent-encoded, when it is not plain ASCII.
  const star = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  let filename = match?.[1] ?? null;
  if (star?.[1]) {
    try {
      filename = decodeURIComponent(star[1]);
    } catch {
      // Keep the ASCII name.
    }
  }
  return { blob: await res.blob(), filename };
}

async function get<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE_URL}${path}`, {
      ...init,
      headers: await headers(init?.headers),
    });
  } catch {
    // `fetch` rejects on DNS failure, a refused connection and a CORS refusal
    // alike, with a message the browser deliberately keeps vague. None of them
    // is a reason to end somebody's session.
    throw new Unreachable('The service could not be reached.');
  }
  if (!res.ok) await refuse(res, path);
  return res.json() as Promise<T>;
}

export const api = {
  health: () => get<Health>('/health'),
  version: () => get<Version>('/version'),

  // Named endpoints rather than a raw-path escape hatch. A screen that can
  // build its own URL is a screen that can forget the /api prefix, the
  // organization header, or the fact that a 409 here means "choose an
  // organization" rather than "conflict".
  attention: <T>(propertyId: string) => get<T>(`/api/properties/${propertyId}/attention`),
  portfolio: <T>() => get<T>('/api/attention'),

  me: () => get<Me>('/api/me'),

  // E12. The field loop is three calls and no more: what am I assigned, what
  // does it ask, and here is what I found. Nothing here can reach a document,
  // an obligation or another building.
  // The limit is the caller's to state. The invitation's checklist asks for a
  // high one because it offers every inspection there is to assign, and the
  // API's default of 50 silently showed it the oldest fifty; every other
  // screen keeps that default, and asking for one here would have widened
  // them all at once.
  inspections: <T>(limit?: number) =>
    get<T>(limit === undefined ? '/api/inspections' : `/api/inspections?limit=${limit}`),
  inspection: <T>(id: string) => get<T>(`/api/inspections/${id}`),
  recordResult: <T>(itemId: string, result: string, note: string | null) =>
    send<T>('PUT', `/api/inspection-items/${itemId}/result`, { result, note }),
  submitInspection: <T>(id: string) => send<T>('POST', `/api/inspections/${id}/submission`, {}),
  // The manager's side of E12: schedule, write the checklist, open the door
  // to one inspector until a date, review, and turn a fail into work.
  propertyInspections: <T>(propertyId: string) =>
    get<T>(`/api/inspections?property_id=${propertyId}`),
  createInspection: <T>(
    propertyId: string,
    body: { title: string; scheduled_for: string | null },
  ) => send<T>('POST', `/api/properties/${propertyId}/inspections`, body),
  addInspectionItem: <T>(
    id: string,
    body: { prompt: string; asset_id: string | null; sort_order: number },
  ) => send<T>('POST', `/api/inspections/${id}/items`, body),
  assignInspector: <T>(id: string, body: { inspector_user_id: string; expires_at: string }) =>
    send<T>('POST', `/api/inspections/${id}/assignments`, body),
  reviewInspection: <T>(id: string) => send<T>('POST', `/api/inspections/${id}/review`, {}),
  promoteFinding: <T>(itemId: string, actionId: string) =>
    send<T>('POST', `/api/inspection-items/${itemId}/promotion`, { action_id: actionId }),

  // E13. The notification centre shows suppressed rows too — a centre that
  // shows only what was delivered cannot answer "why didn't I hear about
  // this", which is the question it will actually be opened to answer.
  notifications: <T>() => get<T>('/api/notifications'),
  advanceNotification: <T>(id: string, toState: string) =>
    send<T>('PUT', `/api/notifications/${id}/lifecycle`, { to_state: toState }),

  propertyCalendar: <T>(propertyId: string, types: string[]) =>
    get<T>(`/api/properties/${propertyId}/calendar${typeQuery(types)}`),
  myCalendar: <T>(types: string[]) => get<T>(`/api/my-work/calendar${typeQuery(types)}`),

  // E14. `propertyId` is the removable scope tag, not the boundary — dropping
  // it widens to everything the asker may see, which is the default scope.
  search: <T>(q: string, propertyId?: string) =>
    get<T>(
      `/api/search?q=${encodeURIComponent(q)}` +
        (propertyId ? `&property_id=${encodeURIComponent(propertyId)}` : ''),
    ),
  notificationPreferences: <T>() => get<T>('/api/notifications/preferences'),
  notificationMutes: <T>() => get<T>('/api/notifications/mutes'),
  muteNotifications: <T>(body: {
    category: string;
    reason: string;
    expires_at: string;
    severity_ceiling: string;
    property_id: string | null;
  }) => send<T>('POST', '/api/notifications/mutes', body),
  setNotificationPreference: <T>(body: unknown) =>
    send<T>('PUT', '/api/notifications/preferences', body),

  reportOutputs: <T>() => get<T>('/api/reports/outputs'),
  report: <T>(propertyId: string, kind: string) =>
    get<T>(`/api/properties/${propertyId}/reports/${kind}`),
  export: <T>(table: string) => get<T>(`/api/export/${table}`),
  // Prepared files, and the CSV, fetched with the session: a plain link
  // carries no bearer token, so the CSV link was refused for everybody.
  reportArtifacts: <T>(propertyId: string) =>
    get<T>(`/api/reports/artifacts?property_id=${propertyId}`),
  requestReport: <T>(propertyId: string, outputKind: string, format: 'pdf' | 'csv') =>
    send<T>('POST', `/api/properties/${propertyId}/reports`, { output_kind: outputKind, format }),
  downloadReport: (artifactId: string) => blob(`/api/reports/artifacts/${artifactId}/download`),
  reportCsv: (propertyId: string, outputKind: string) =>
    blob(`/api/properties/${propertyId}/reports/${outputKind}.csv`),

  // The registers. Each returns rows carrying their own basis, so a screen
  // renders the sentence rather than composing one.
  properties: <T>() => get<T>('/api/properties'),
  property: <T>(id: string) => get<T>(`/api/properties/${id}`),
  completeness: <T>(id: string) => get<T>(`/api/properties/${id}/completeness`),
  documents: <T>(propertyId: string) => get<T>(`/api/documents?property_id=${propertyId}`),
  assets: <T>(propertyId: string) => get<T>(`/api/assets?property_id=${propertyId}`),
  obligations: <T>(propertyId: string) => get<T>(`/api/obligations?property_id=${propertyId}`),
  actions: <T>(propertyId: string, openOnly = true) =>
    get<T>(`/api/actions?property_id=${propertyId}&open_only=${openOnly}`),
  myWork: <T>() => get<T>('/api/my-work'),
  capitalPlans: <T>(propertyId: string) => get<T>(`/api/properties/${propertyId}/capital-plans`),
  // E10. A line's cost travels with source, region, date and firmness or not
  // at all; the API refuses a bare figure at three layers.
  capitalPlan: <T>(planId: string) => get<T>(`/api/capital-plans/${planId}`),
  createCapitalPlan: <T>(
    propertyId: string,
    body: { title: string; prepared_on: string | null; source_document_id: string | null },
  ) => send<T>('POST', `/api/properties/${propertyId}/capital-plans`, body),
  addCapitalLine: <T>(planId: string, body: Record<string, unknown>) =>
    send<T>('POST', `/api/capital-plans/${planId}/lines`, body),
  users: <T>() => get<T>('/api/users'),
  user: <T>(id: string) => get<T>(`/api/users/${id}`),
  // E01-S05 to S09. Refused by the API without MANAGE_USERS (property access:
  // MANAGE_PROPERTY_ACCESS); the screens show the refusal, not a hidden button.
  invitations: <T>() => get<T>('/api/users/invitations'),
  // `propertyIds`: for a Manager, the properties they can open once they join.
  // `inspections`: for an Inspector, the inspections they are assigned on
  // joining, with the date that access ends.
  inviteUser: <T>(
    email: string,
    role: string,
    propertyIds: string[] = [],
    inspections: { ids: string[]; until: string | null } = { ids: [], until: null },
  ) =>
    send<T>('POST', '/api/users/invitations', {
      email,
      role,
      property_ids: propertyIds,
      inspection_ids: inspections.ids,
      inspection_access_until: inspections.until,
    }),
  cancelInvitation: <T>(id: string) =>
    send<T>('POST', `/api/users/invitations/${id}/cancel`, {}),
  changeUserRole: <T>(id: string, role: string) =>
    send<T>('PATCH', `/api/users/${id}/role`, { role }),
  setUserActive: <T>(id: string, active: boolean) =>
    send<T>('PATCH', `/api/users/${id}/active`, { active }),
  setPropertyAccess: <T>(id: string, propertyIds: string[]) =>
    send<T>('PUT', `/api/users/${id}/property-access`, { property_ids: propertyIds }),
  requestEmailChange: <T>(id: string, newEmail: string) =>
    send<T>('POST', `/api/users/${id}/email-change`, { new_email: newEmail }),
  // Both unauthenticated on the API side: the token is the proof, and the
  // organization travels with it in the link.
  verifyEmailChange: <T>(token: string, organizationId: string) =>
    send<T>('POST', '/api/email-changes/verify', { token, organization_id: organizationId }),
  acceptInvitation: <T>(token: string, organizationId: string, displayName: string | null) =>
    send<T>('POST', '/api/invitations/accept', {
      token,
      organization_id: organizationId,
      display_name: displayName,
    }),

  // The trust spine's surfaces. Named endpoints, never a raw path: a screen
  // that can build its own URL is a screen that can forget the /api prefix or
  // the fact that a 409 here means "choose an organization".
  candidates: <T>() => get<T>('/api/candidates'),
  candidate: <T>(id: string) => get<T>(`/api/candidates/${id}`),
  reviewCandidate: <T>(
    id: string,
    decision: string,
    body: { note: string | null; value_text: string | null },
  ) =>
    send<T>('POST', `/api/candidates/${id}/review`, {
      decision,
      reason: body.note,
      // Only sent for an edit. A correction credited to the model would
      // misattribute the value; an unchanged value sent as a correction would
      // do the same in reverse.
      corrected_text: body.value_text,
    }),
  conflicts: <T>(propertyId?: string) =>
    get<T>(`/api/conflicts${propertyId ? `?property_id=${propertyId}` : ''}`),
  conflict: <T>(id: string) => get<T>(`/api/conflicts/${id}`),
  resolveConflict: <T>(id: string, outcome: string, reason: string) =>
    send<T>('POST', `/api/conflicts/${id}/resolution`, { outcome, reason }),
  documentRequests: <T>(propertyId?: string) =>
    get<T>(`/api/document-requests${propertyId ? `?property_id=${propertyId}` : ''}`),

  // The detail screens.
  action: <T>(id: string) => get<T>(`/api/actions/${id}`),

  /* E08-F01 — create an action from the context that justified it.
     `source_type` and `source_id` are not optional decoration: E08-S07 makes
     the originating object part of the record, so a completed action can still
     answer "what was this for" after the issue that produced it has closed. */
  createAction: <T>(
    propertyId: string,
    body: {
      title: string;
      priority: string;
      due_date: string | null;
      needs_attention_by: string | null;
      source_type: string;
      source_id: string;
    },
  ) => send<T>('POST', `/api/properties/${propertyId}/actions`, body),

  /* Acting on an action. Seven writes the API has served since Sprint 6 with
     nothing on the other end — see docs/functional-gap-2026-09-10.md.

     ONE ROUTE PER ACT, not a generic patch, because the API is built that way
     on purpose: completing, deferring, blocking and cancelling carry different
     evidence requirements, and a field update is how "deferred" comes to look
     like "done" in a board report. Mirroring that shape here keeps the refusal
     the server sends meaningful. */
  transitionAction: <T>(id: string, target: string, reason?: string, newDueDate?: string) =>
    send<T>('POST', `/api/actions/${id}/transition`, {
      target,
      reason: reason || null,
      new_due_date: newDueDate || null,
    }),
  assignAction: <T>(id: string, toUserId: string, reason: string) =>
    send<T>('POST', `/api/actions/${id}/assignee`, {
      to_user_id: toUserId,
      reason,
    }),
  addActionEvidence: <T>(id: string, documentId: string | null, note: string) =>
    send<T>('POST', `/api/actions/${id}/evidence`, {
      document_id: documentId,
      note: note || null,
    }),
  /* Deferral. `expires_at` is required and has no default: an accepted risk
     that never expires is a decision nobody revisits, which is the failure
     this record exists to prevent. */
  acceptRisk: <T>(id: string, reason: string, expiresAt: string) =>
    send<T>('POST', `/api/actions/${id}/accepted-risk`, {
      reason,
      expires_at: expiresAt,
    }),
  /* E08-S10 / S11 / E07-S18 — planning around an action. The people list is
     the narrow read assigning and escalating need, not the user directory. */
  propertyPeople: <T>(propertyId: string) => get<T>(`/api/properties/${propertyId}/people`),
  escalateAction: <T>(id: string, watcherUserId: string, reason: string) =>
    send<T>('POST', `/api/actions/${id}/escalation`, { watcher_user_id: watcherUserId, reason }),
  addMilestone: <T>(id: string, body: { title: string; due_date: string | null }) =>
    send<T>('POST', `/api/actions/${id}/milestones`, body),
  completeMilestone: <T>(id: string, milestoneId: string) =>
    send<T>('POST', `/api/actions/${id}/milestones/${milestoneId}/completion`, {}),
  addActionDependency: <T>(id: string, dependsOnActionId: string) =>
    send<T>('POST', `/api/actions/${id}/dependencies`, { depends_on_action_id: dependsOnActionId }),
  addActionNote: <T>(id: string, body: string) =>
    send<T>('POST', `/api/actions/${id}/notes`, { body }),
  requestApproval: <T>(id: string) => send<T>('POST', `/api/actions/${id}/approval-request`, {}),
  decideApproval: <T>(approvalId: string, approved: boolean, reason: string) =>
    send<T>('POST', `/api/approvals/${approvalId}/decision`, {
      approved,
      reason: reason || null,
    }),
  asset: <T>(id: string) => get<T>(`/api/assets/${id}`),

  /* E05 — assets. Condition changes only through `overrideAsset`, with a
     reason; a cost only with its source, region and date. */
  createAsset: <T>(
    propertyId: string,
    body: {
      name: string;
      asset_type: string;
      location: string | null;
      condition: string;
      criticality: string;
      installed_on: string | null;
      expected_life_years: number | null;
    },
  ) => send<T>('POST', `/api/properties/${propertyId}/assets`, body),
  updateAsset: <T>(id: string, changes: Record<string, string | number | null>) =>
    send<T>('PATCH', `/api/assets/${id}`, changes),
  setAssetCost: <T>(
    id: string,
    body: { amount: number; source: string; region: string; as_of: string },
  ) => send<T>('PUT', `/api/assets/${id}/cost`, body),
  overrideAsset: <T>(id: string, band: string, reason: string) =>
    send<T>('POST', `/api/assets/${id}/override`, { band, reason }),

  /* E06 — obligations. */
  jurisdictionRules: <T>(jurisdiction: string) =>
    get<T>(`/api/jurisdictions/${jurisdiction}/rules`),
  createObligation: <T>(
    propertyId: string,
    body: {
      title: string;
      is_mandatory: boolean;
      jurisdiction_rule_id: string | null;
      recommendation_source: string | null;
      asset_id: string | null;
    },
  ) => send<T>('POST', `/api/properties/${propertyId}/obligations`, body),
  addOccurrence: <T>(
    id: string,
    body: { due_date: string | null; needs_attention_by: string | null },
  ) => send<T>('POST', `/api/obligations/${id}/occurrences`, body),
  grantObligationException: <T>(id: string, reason: string, expiresAt: string) =>
    send<T>('POST', `/api/obligations/${id}/exception`, { reason, expires_at: expiresAt }),
  completeOccurrence: <T>(
    obligationId: string,
    occurrenceId: string,
    body: { evidence_document_id: string | null; note: string | null },
  ) =>
    send<T>(
      'POST',
      `/api/obligations/${obligationId}/occurrences/${occurrenceId}/completion`,
      body,
    ),
  obligation: <T>(id: string) => get<T>(`/api/obligations/${id}`),
  vendors: <T>() => get<T>('/api/vendors'),
  // E03-S09 / SO-07. Owners and Admins; every write carries a reason.
  retentionPolicies: <T>() => get<T>('/api/retention/policies'),
  setRetentionPolicy: <T>(body: Record<string, unknown>) =>
    send<T>('PUT', '/api/retention/policies', body),
  legalHolds: <T>(includeReleased = false) =>
    get<T>(`/api/retention/holds?include_released=${includeReleased}`),
  placeHold: <T>(body: Record<string, unknown>) => send<T>('POST', '/api/retention/holds', body),
  releaseHold: <T>(id: string, reason: string) =>
    send<T>('POST', `/api/retention/holds/${id}/release`, { reason }),
  deletionRequests: <T>() => get<T>('/api/retention/deletions'),
  requestDeletion: <T>(body: Record<string, unknown>) =>
    send<T>('POST', '/api/retention/deletions', body),
  decideDeletion: <T>(id: string, approve: boolean, reason: string) =>
    send<T>('POST', `/api/retention/deletions/${id}/decision`, { approve, reason }),
  executeDeletion: <T>(id: string) =>
    send<T>('POST', `/api/retention/deletions/${id}/execution`, {}),
  vendor: <T>(id: string) => get<T>(`/api/vendors/${id}`),
  // E09, thin: who they are, what they did where, and what proves it.
  createVendor: <T>(body: { name: string; trade: string | null; contact_email: string | null }) =>
    send<T>('POST', '/api/vendors', body),
  recordVendorInsurance: <T>(id: string, body: { expires_on: string; document_id: string }) =>
    send<T>('PUT', `/api/vendors/${id}/insurance`, body),
  engageVendor: <T>(id: string, body: Record<string, unknown>) =>
    send<T>('POST', `/api/vendors/${id}/engagements`, body),
  // B5-04. Addressed by its own id rather than through the action, because a
  // notification about a lapsing acceptance points at the acceptance.
  acceptedRisk: <T>(id: string) => get<T>(`/api/accepted-risks/${id}`),

  // B3-20. Multipart, not JSON: the bytes are the point, and the location is
  // a state travelling beside them rather than a pair of nullable numbers.
  capturePhoto: <T>(
    itemId: string,
    file: File,
    location: { state: string; latitude?: number; longitude?: number },
  ) => {
    const form = new FormData();
    form.append('file', file);
    form.append('location_state', location.state);
    if (location.latitude !== undefined) form.append('latitude', String(location.latitude));
    if (location.longitude !== undefined) form.append('longitude', String(location.longitude));
    return upload<T>(`/api/inspection-items/${itemId}/photo`, form);
  },

  createProperty: <T>(body: {
    name: string;
    jurisdiction: string;
    plan_number?: string | null;
    address?: {
      line1: string | null;
      city: string | null;
      region: string | null;
      postal_code: string | null;
    };
    strata_lot_count?: number | null;
  }) => send<T>('POST', '/api/properties', body),

  /* PI-03 — a profile fact, stated, not applicable, or explicitly Unknown. */
  putFact: <T>(
    propertyId: string,
    factKey: string,
    body: {
      state: string;
      value_text: string | null;
      value_number: number | null;
      value_date: string | null;
      as_of: string | null;
    },
  ) => send<T>('PUT', `/api/properties/${propertyId}/facts/${encodeURIComponent(factKey)}`, body),
  /* PI-06 — a stage done, deliberately skipped, or reopened. */
  updateOnboarding: <T>(propertyId: string, step: string, action: 'complete' | 'skip' | 'reopen') =>
    send<T>('POST', `/api/properties/${propertyId}/onboarding`, { step, action }),

  /* E03 — putting a document in, and everything that happens to it after.

     TWO CALLS TO UPLOAD, on purpose, mirroring the API: `createDocument`
     reserves the record and `uploadDocumentContent` streams the bytes and
     queues the malware scan. A document whose create succeeded and whose bytes
     did not is visible as exactly that, and retrying sends the bytes again
     rather than creating a second record. */
  document: <T>(id: string) => get<T>(`/api/documents/${id}`),
  createDocument: <T>(body: {
    property_id: string;
    title: string;
    filename: string;
    content_type: string;
    sensitivity: string;
    document_type: string | null;
  }) => send<T>('POST', '/api/documents', body),
  uploadDocumentContent: <T>(id: string, file: File, contentType?: string) => {
    const form = new FormData();
    // The type the product checked, not the browser's guess. A file recognised
    // by its extension has an empty type, which is sent as octet-stream and
    // refused.
    form.append(
      'file',
      contentType && file.type !== contentType
        ? new File([file], file.name, { type: contentType })
        : file,
    );
    return upload<T>(`/api/documents/${id}/content`, form);
  },
  /* Only the fields passed are changed; `null` clears one. */
  updateDocument: <T>(id: string, changes: Record<string, string | null>) =>
    send<T>('PATCH', `/api/documents/${id}`, changes),
  /* Lifecycle and trust only. Processing moves through `retryDocument`, which
     queues the work — a bare state change on that axis was the retry that
     never retried. */
  transitionDocument: <T>(id: string, axis: 'lifecycle' | 'trust', target: string) =>
    send<T>('POST', `/api/documents/${id}/transitions`, { axis, target }),
  retryDocument: <T>(id: string) => send<T>('POST', `/api/documents/${id}/retry`, {}),
  supersedeDocument: <T>(id: string, byDocumentId: string) =>
    send<T>('POST', `/api/documents/${id}/supersession`, { by_document_id: byDocumentId }),
  linkDocument: <T>(id: string, targetType: string, targetId: string) =>
    send<T>('POST', `/api/documents/${id}/links`, {
      target_type: targetType,
      target_id: targetId,
    }),
  grantDocument: <T>(id: string, userId: string, reason: string) =>
    send<T>('POST', `/api/documents/${id}/grants`, { user_id: userId, reason }),
  revokeDocumentGrant: <T>(id: string, userId: string) =>
    send<T>('DELETE', `/api/documents/${id}/grants/${userId}`, {}),
  downloadDocument: (id: string, version?: number) =>
    blob(`/api/documents/${id}/content${version ? `?version=${version}` : ''}`),

  /* DM-15 — asking for a document, receiving it, and deciding about it. */
  createDocumentRequest: <T>(body: {
    property_id: string;
    document_type: string;
    requested_from: string | null;
    due_by: string | null;
    notes: string | null;
  }) => send<T>('POST', '/api/document-requests', body),
  submitToRequest: <T>(requestId: string, documentId: string) =>
    send<T>('POST', `/api/document-requests/${requestId}/submission`, {
      document_id: documentId,
    }),
  reviewRequest: <T>(requestId: string, decision: string, note: string) =>
    send<T>('POST', `/api/document-requests/${requestId}/review`, {
      decision,
      note: note || null,
    }),
  cancelRequest: <T>(requestId: string, reason: string) =>
    send<T>('POST', `/api/document-requests/${requestId}/cancellation`, { reason }),

  // B1-04. The subject type is part of the path because it changes what the
  // resource is, not how it is filtered.
  issue: <T>(propertyId: string, subjectType: string, subjectId: string) =>
    get<T>(`/api/properties/${propertyId}/issues/${subjectType}/${subjectId}`),
};

function typeQuery(types: string[]): string {
  return types.length ? `?${types.map((t) => `record_type=${t}`).join('&')}` : '';
}
